import { getProjectId, getPublicationElements } from './utility'
import ky from 'ky'
import { WebStorage } from './storage'
import { EventType } from '@noticeable-app/noticeable-common'

export function initializePublicationLinkClickReporting() {
    const publicationElements = getPublicationElements()
    for (const publicationElement of publicationElements) {
        const postContentElement = publicationElement.getElementsByClassName('post-content')[0]
        const publicationId = publicationElement.dataset.publicationId as string
        const linkElements = postContentElement.getElementsByTagName('a')

        for (let j = 0; j < linkElements.length; j++) {
            ;(function (publicationId) {
                linkElements[j].addEventListener('auxclick', function (e: MouseEventInit) {
                    if (e.button === 1) {
                        reportLinkClick(publicationId)
                    }
                })

                linkElements[j].addEventListener('click', function (e: MouseEventInit) {
                    reportLinkClick(publicationId)
                })
            })(publicationId)
        }
    }
}

function reportLinkClick(publicationId: string) {
    return ky.post(window.noticeable.pipelineBaseUrl + '/m/nlc', {
        json: {
            p: window.noticeable.projectId,
            q: publicationId,
        },
    })
}

export function restoreReactions() {
    const publicationElements = getPublicationElements()
    for (const publicationElement of publicationElements) {
        const publicationId = publicationElement.dataset.publicationId as string
        const reaction = retrieveReactionFromLocalStorage(publicationId)

        if (reaction) {
            selectReaction(publicationElement, reaction)
        }

        const postContentReactionsElement = firstElementByClassName(
            publicationElement,
            'post-content-reactions',
        ) as HTMLElement
        if (postContentReactionsElement) {
            postContentReactionsElement.style.display = 'block'
        }
    }
}

export function sendBadReaction(projectId: string, publicationId: string) {
    sendReaction(projectId, publicationId, 'bad')
}

export function sendNeutralReaction(projectId: string, publicationId: string) {
    sendReaction(projectId, publicationId, 'neutral')
}

export function sendGoodReaction(projectId: string, publicationId: string) {
    sendReaction(projectId, publicationId, 'good')
}

export function sendReaction(projectId: string, publicationId: string, type: string) {
    const publication = document.querySelectorAll('.publication[data-id="' + publicationId + '"]')[0] as HTMLElement
    if (!publication) {
        return
    }

    const emojiButtons = publication.getElementsByClassName(type + '-reaction')
    if (!emojiButtons) {
        return
    }

    const emojiButton = emojiButtons[0]

    if (emojiButton && emojiButton.getAttribute('selected') !== 'true') {
        const projectId = getProjectId()
        const userId = WebStorage.getUserId(projectId)
        const userTraits = WebStorage.getUserTraits(projectId)

        const reaction = type.charAt(0)
        ky.post(window.noticeable.pipelineBaseUrl + '/r', {
            json: {
                p: projectId,
                q: publicationId,
                r: reaction,
                s: WebStorage.getUserSsoToken(projectId),
                t: {
                    a: userTraits.avatarUrl,
                    d: userTraits.displayName,
                    e: userTraits.email,
                    f: userTraits.firstName,
                    i: userId,
                    l: userTraits.lastName,
                },
            },
        })

        const previousReaction = retrieveReactionFromLocalStorage(publicationId)
        storeReactionToLocalStorage(publicationId, reaction)
        selectReaction(publication, type)

        window.noticeable.parentWindow?.dispatchEvent(
            'widget:publication:reaction:' + (previousReaction ? 'updated' : 'created'),
            {
                projectId: projectId,
                publicationId: publicationId,
                reaction: reactionLongForm(reaction),
                userTraits: {
                    /** @deprecated: use avatarUrl instead */
                    avatar: userTraits.avatarUrl,
                    avatarUrl: userTraits.avatarUrl,
                    displayName: userTraits.displayName,
                    email: userTraits.email,
                    fistName: userTraits.firstName,
                    /** @deprecated: use displayName instead */
                    fullName: userTraits.displayName,
                    id: userId,
                    lastName: userTraits.lastName,
                },
            },
        )
    }
}

function arraysEqual(arr1, arr2) {
    if (arr1.length !== arr2.length) {
        return false
    }
    return arr1.every((value, index) => Math.abs(value - arr2[index]) < 0.01)
}

let intersectionObserver

export function setupIntersectionObserver(): void {
    const preferenceThresholds = window.noticeable?.sdkPreferences?.events?.publicationVisibilityThresholds ?? [0.5]

    if (intersectionObserver) {
        if (arraysEqual(preferenceThresholds, intersectionObserver.thresholds)) {
            return
        } else {
            intersectionObserver.disconnect()
            intersectionObserver = undefined
        }
    }

    intersectionObserver = new IntersectionObserver(
        (entries) => {
            entries.forEach((entry) => {
                const publicationElement = entry.target as HTMLElement

                const publication = {
                    id: publicationElement.getAttribute('data-id'),
                    slug: publicationElement.getAttribute('data-slug'),
                    title: publicationElement.getAttribute('data-title'),
                }

                if (!publication.id) return

                if (entry.isIntersecting) {
                    // Publication became visible
                    handlePublicationVisibilityChange(publication, entry.intersectionRatio, true)
                } else {
                    // Publication is no longer visible
                    handlePublicationVisibilityChange(publication, entry.intersectionRatio, false)
                }
            })
        },
        {
            threshold: preferenceThresholds,
        },
    )

    const publicationElements: HTMLElement[] = [...document.querySelectorAll<HTMLElement>('article.listing-post')]

    if (publicationElements.length > 0) {
        for (const publicationElement of publicationElements) {
            intersectionObserver.observe(publicationElement)
        }
    }
}

function handlePublicationVisibilityChange(publication: object, visibilityThreshold: number, isVisible: boolean): void {
    window.noticeable.parentWindow?.dispatchEvent(EventType.WIDGET_PUBLICATION_VISIBILITY_CHANGED, {
        publication: publication,
        visibilityThreshold: visibilityThreshold,
        visible: isVisible,
    })
}

function reactionLongForm(reaction: string): string | undefined {
    switch (reaction) {
        case 'b':
            return 'bad'
        case 'g':
            return 'good'
        case 'n':
            return 'neutral'
        default:
            return undefined
    }
}

function selectReaction(publication: HTMLElement, type: string) {
    const badReactionElement = firstElementByClassName(publication, 'bad-reaction')
    const goodReactionElement = firstElementByClassName(publication, 'good-reaction')
    const neutralReactionElement = firstElementByClassName(publication, 'neutral-reaction')

    if (badReactionElement) {
        badReactionElement.setAttribute('selected', toString('bad' === type || 'b' === type))
    }

    if (goodReactionElement) {
        goodReactionElement.setAttribute('selected', toString('good' === type || 'g' === type))
    }

    if (neutralReactionElement) {
        neutralReactionElement.setAttribute('selected', toString('neutral' === type || 'n' === type))
    }
}

function retrieveReactionFromLocalStorage(publicationId) {
    const existingReaction = WebStorage.getStorage().getItem('noticeable:publications:' + publicationId + ':reaction')

    if (existingReaction === 'b' || existingReaction === 'g' || existingReaction === 'n') {
        return existingReaction
    }
}

function storeReactionToLocalStorage(publicationId: string, reaction: string) {
    WebStorage.getStorage().setItem('noticeable:publications:' + publicationId + ':reaction', reaction)
}

function firstElementByClassName(parent: HTMLElement, className: string) {
    const elements = parent.getElementsByClassName(className)
    if (!elements || elements.length === 0) {
        return null
    }

    return elements[0]
}

function toString(b: boolean) {
    return b ? 'true' : 'false'
}
